import React, { useLayoutEffect, useRef, useState } from "react";
import { styled, keyframes } from "@mui/material/styles";

const marquee = keyframes`
  0%, 15% {
    transform: translateX(0);
  }
  45%, 55% {
    transform: translateX(calc(var(--scroll-distance) * -1));
  }
  85%, 100% {
    transform: translateX(0);
  }
`;

type MarqueeTextPropsT = {
  children: React.ReactNode;
  className?: string;
  duration?: number;
};

type InnerSpanPropsT = {
  $isScrolling: boolean;
  $duration: number;
};

const Wrapper = styled("div")({
  overflow: "hidden",
  width: "100%",
});

const InnerSpan = styled("span", {
  shouldForwardProp: (prop) => prop !== "$isScrolling" && prop !== "$duration",
})<InnerSpanPropsT>(({ $isScrolling, $duration }) => ({
  display: "inline-block",
  whiteSpace: "nowrap",
  verticalAlign: "top",
  textOverflow: "ellipsis",
  maxWidth: $isScrolling ? "none" : "100%",
  overflow: $isScrolling ? "visible" : "hidden",
  ...($isScrolling && {
    animation: `${marquee} ${$duration}s ease-in-out infinite`,
    "&:hover, &:focus-within": {
      animationPlayState: "paused",
    },
  }),
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
}));

const MarqueeText = ({ children, className, duration = 8 }: MarqueeTextPropsT) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLSpanElement>(null);
  const [overflowPx, setOverflowPx] = useState(0);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const inner = innerRef.current;
    if (!wrapper || !inner) return undefined;

    const measure = () => {
      if (!wrapperRef.current || !innerRef.current) return;
      const diff = innerRef.current.scrollWidth - wrapperRef.current.clientWidth;
      setOverflowPx(diff > 0 ? diff : 0);
    };

    measure();
    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [children]);

  const isScrolling = overflowPx > 0;

  return (
    <Wrapper ref={wrapperRef} className={className}>
      <InnerSpan
        ref={innerRef}
        $isScrolling={isScrolling}
        $duration={duration}
        style={isScrolling ? ({ "--scroll-distance": `${overflowPx}px` } as React.CSSProperties) : undefined}
      >
        {children}
      </InnerSpan>
    </Wrapper>
  );
};

export default MarqueeText;
